/**
 * Pure helpers for the Ports and Interfaces sections (issue #52).
 *
 * Apart from the components for the reason `parts-model.ts` is: each answers a
 * question whose wrong answer renders something plausible - a port shown
 * without its `~`, a Connect button enabled with nothing to connect.
 */

import type { InterfaceFeature, PartPort } from '../../schema'
import type { WritePermission } from './parts-model'

/** A port's type as the model writes it, with the `~` a conjugated one carries. */
export function portType(port: Pick<PartPort, 'type_name' | 'is_conjugate'>): string {
  if (!port.type_name) return ''
  return `${port.is_conjugate ? '~' : ''}${port.type_name}`
}

/** Why "Connect ports" is unavailable, or '' when it is not. */
export function connectRefusal(
  permission: WritePermission,
  isDefinition: boolean,
  endpoints: number,
): string {
  if (!permission.allowed) return permission.reason
  if (!isDefinition) return 'Interfaces are declared inside a part definition, not a usage.'
  if (endpoints < 2) {
    return 'An interface joins two ports. Give this definition, or the parts inside it, ' +
      'at least two ports first.'
  }
  return ''
}

/**
 * The direction a feature crosses a PORT in, which is not always the direction
 * its port def states.
 *
 * `~TelemetryPort` reverses every direction on `TelemetryPort`: the def says
 * `out telemetry`, and on the conjugated port telemetry comes IN. Showing the
 * def's own word on the receiving side would label both ends of an interface
 * "out" - which is exactly the reading an ICD must not invite.
 */
export function effectiveDirection(direction: string, conjugate: boolean): string {
  if (!conjugate) return direction
  if (direction === 'out') return 'in'
  if (direction === 'in') return 'out'
  return direction
}

/**
 * What crosses a port on one side, in words: "sends telemetry; receives ack".
 *
 * Tested directly, because it is the one place a reversed side would be caught:
 * the sentence reads perfectly well either way round.
 */
export function sideSummary(features: readonly InterfaceFeature[], conjugate: boolean): string {
  const sends: string[] = []
  const receives: string[] = []
  for (const feature of features) {
    if (!feature.direction) continue
    const seen = effectiveDirection(feature.direction, conjugate)
    if (seen === 'out' || seen === 'inout') sends.push(feature.name)
    if (seen === 'in' || seen === 'inout') receives.push(feature.name)
  }
  const parts = [
    sends.length ? `sends ${sends.join(', ')}` : '',
    receives.length ? `receives ${receives.join(', ')}` : '',
  ].filter(Boolean)
  return parts.join('; ')
}
